/**
 * BillingPage
 * Main billing page with subscription management and plan selection
 */
import { useState, useCallback, useEffect, useRef } from 'react';
import { useSearchParams } from 'react-router-dom';
import { useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';
import Card, { CardHeader } from '@/components/ui/Card';
import Alert from '@/components/ui/Alert';
import Spinner from '@/components/ui/Spinner';
import {
  ChangePlanModal,
  CurrentSubscription,
  PlanSelector,
  PaymentHistory,
} from '@/components/billing';
import { ContactLink } from '@/components/contact';
import {
  usePlans,
  useSubscription,
  useCreateCheckout,
  useCancelSubscription,
  useResumeSubscription,
  useBillingPortal,
  useChangePlan,
  billingKeys,
} from '@/hooks/useBilling';
import { billingApi } from '@/services/billing';
import { useAuthStore } from '@/store/authStore';
import { isSubscriptionExistsError, type Plan } from '@/types/billing';

export default function BillingPage() {
  const [searchParams, setSearchParams] = useSearchParams();
  const [loadingPlanId, setLoadingPlanId] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [activationSuccess, setActivationSuccess] = useState(false);
  // WP-BILLING-19 Part B review L-4: the plan awaiting confirmation
  const [planToConfirm, setPlanToConfirm] = useState<Plan | null>(null);
  const queryClient = useQueryClient();
  const user = useAuthStore((state) => state.user);

  // WP-BUG-BILLING-SYNC: State for checkout sync fallback
  const [isSyncing, setIsSyncing] = useState(false);
  const [syncComplete, setSyncComplete] = useState(false);
  const syncAttemptedRef = useRef(false);

  // Check for success/cancel from Stripe redirect
  const checkoutSuccess = searchParams.get('checkout') === 'success';
  const checkoutCanceled = searchParams.get('checkout') === 'canceled';
  const sessionId = searchParams.get('session_id');

  // Dismissing a Stripe-return message removes ?checkout and ?session_id, so it
  // is gone and a reload does not show it again. This must go through the
  // router: the alerts are rendered from useSearchParams(), and the previous
  // window.history.replaceState() changed the URL without React Router
  // noticing, so Dismiss did nothing (staging E2E run 37560419552).
  const dismissCheckoutMessage = useCallback(() => {
    setSearchParams(
      (previous) => {
        const next = new URLSearchParams(previous);
        next.delete('checkout');
        next.delete('session_id');
        return next;
      },
      { replace: true }
    );
  }, [setSearchParams]);

  // Data fetching - moved before effects that need subscription data
  const { data: plans, isLoading: plansLoading, error: plansError } = usePlans();
  const { data: subscriptionData, isLoading: subscriptionLoading, error: subscriptionError } = useSubscription();

  // Derive subscription status for use in effects. hasSubscription: the
  // subscription grants access (used to confirm a checkout went through).
  const hasSubscription = subscriptionData?.hasActiveSubscription;

  // WP-BUG-BILLING-SYNC: Sync subscription when returning from Stripe checkout
  // This is a fallback mechanism for when webhooks are delayed
  useEffect(() => {
    const syncSubscription = async () => {
      // Only sync if:
      // 1. We have a session_id from Stripe redirect
      // 2. Checkout was successful
      // 3. We haven't already attempted sync (prevents double-sync on refetch)
      if (!sessionId || !checkoutSuccess || syncAttemptedRef.current) {
        return;
      }

      syncAttemptedRef.current = true;
      setIsSyncing(true);

      try {
        // Call backend to sync subscription from checkout session
        await billingApi.syncFromCheckout(sessionId);

        // Refresh subscription and entitlements data
        await queryClient.invalidateQueries({ queryKey: billingKeys.subscription() });
        await queryClient.invalidateQueries({ queryKey: billingKeys.entitlements() });

        setSyncComplete(true);
      } catch (err) {
        // Even if sync fails, subscription may still sync via webhook
        // Don't show error to user - just log it
        console.warn('Checkout sync fallback failed (webhook may still succeed):', err);
        // Still mark as complete so we don't keep retrying
        setSyncComplete(true);
      } finally {
        setIsSyncing(false);
      }
    };

    syncSubscription();
  }, [sessionId, checkoutSuccess, queryClient]);

  // WP-BUG-BILLING-SYNC: Poll for subscription if sync completed but no subscription yet
  // This handles the case where webhook is slightly delayed
  useEffect(() => {
    if (!checkoutSuccess || !syncComplete || hasSubscription || isSyncing) {
      return;
    }

    // Poll every 2 seconds for up to 10 seconds
    let attempts = 0;
    const maxAttempts = 5;

    const pollInterval = setInterval(async () => {
      attempts++;
      await queryClient.invalidateQueries({ queryKey: billingKeys.subscription() });

      if (attempts >= maxAttempts) {
        clearInterval(pollInterval);
      }
    }, 2000);

    return () => clearInterval(pollInterval);
  }, [checkoutSuccess, syncComplete, hasSubscription, isSyncing, queryClient]);

  // Mutations
  const createCheckout = useCreateCheckout();
  const cancelSubscription = useCancelSubscription();
  const resumeSubscription = useResumeSubscription();
  const billingPortal = useBillingPortal();
  const changePlan = useChangePlan();

  // Handle plan selection - redirect to Stripe Checkout or handle instant activation
  const handleSelectPlan = useCallback(
    async (plan: Plan, couponCode?: string) => {
      setError(null);
      setLoadingPlanId(plan.id);

      try {
        const currentUrl = window.location.origin + window.location.pathname;
        // WP-BUG-BILLING-SYNC: Include session_id in success URL for fallback sync
        // {CHECKOUT_SESSION_ID} is a Stripe template variable that gets replaced
        // with the actual session ID when redirecting back
        const result = await createCheckout.mutateAsync({
          planId: plan.id,
          successUrl: `${currentUrl}?checkout=success&session_id={CHECKOUT_SESSION_ID}`,
          cancelUrl: `${currentUrl}?checkout=canceled`,
          couponCode,
        });

        // WP-BILLING-11: Check for FREE_FOREVER instant activation
        if (result.activated) {
          // Subscription was activated instantly (FREE_FOREVER coupon)
          toast.success(result.message || 'Premium access activated!');
          setActivationSuccess(true);
          setLoadingPlanId(null);

          // Refresh subscription and entitlements data
          await queryClient.invalidateQueries({ queryKey: billingKeys.subscription() });
          await queryClient.invalidateQueries({ queryKey: billingKeys.entitlements() });
          return;
        }

        // Standard Stripe Checkout flow
        if (result.checkoutUrl) {
          window.location.href = result.checkoutUrl;
        } else {
          // Unexpected response - no activation and no checkout URL
          throw new Error('Checkout session creation failed');
        }
      } catch (err) {
        setLoadingPlanId(null);
        if (isSubscriptionExistsError(err)) {
          // WP-BILLING-19: the household already has a subscription (another
          // tab, or a purchase that just completed). Show it; don't sell another.
          setError('This household already has a subscription. You can manage it below.');
          await queryClient.invalidateQueries({ queryKey: billingKeys.subscription() });
          return;
        }
        const message = err instanceof Error ? err.message : 'Failed to start checkout';
        setError(message);
      }
    },
    [createCheckout, queryClient]
  );

  // WP-BILLING-19 Part B: choosing a plan only opens the confirmation (review
  // L-4); the change happens when the owner confirms what it costs.
  const handleChooseNewPlan = useCallback((plan: Plan) => {
    setError(null);
    setPlanToConfirm(plan);
  }, []);

  const handleChangePlan = useCallback(
    async (plan: Plan) => {
      setLoadingPlanId(plan.id);
      try {
        await changePlan.mutateAsync(plan.id);
        toast.success(`Your plan is now ${plan.name}.`);
      } catch (err) {
        setError(err instanceof Error ? err.message : 'Failed to change plan');
      } finally {
        setLoadingPlanId(null);
        setPlanToConfirm(null);
      }
    },
    [changePlan]
  );

  // Handle subscription cancellation
  const handleCancelSubscription = useCallback(
    async (immediately: boolean, reason?: string) => {
      setError(null);
      try {
        await cancelSubscription.mutateAsync({ immediately, reason });
      } catch (err) {
        const message = err instanceof Error ? err.message : 'Failed to cancel subscription';
        setError(message);
        throw err;
      }
    },
    [cancelSubscription]
  );

  // Handle subscription resume
  const handleResumeSubscription = useCallback(async () => {
    setError(null);
    try {
      await resumeSubscription.mutateAsync();
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Failed to resume subscription';
      setError(message);
      throw err;
    }
  }, [resumeSubscription]);

  // Handle manage billing - redirect to Stripe Portal
  const handleManageBilling = useCallback(async () => {
    setError(null);
    try {
      const result = await billingPortal.mutateAsync(window.location.href);
      window.location.href = result.url;
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Failed to open billing portal';
      setError(message);
    }
  }, [billingPortal]);

  // Loading state
  if (plansLoading || subscriptionLoading) {
    return (
      <div className="max-w-6xl mx-auto">
        <div className="flex justify-center py-12">
          <Spinner size="lg" />
        </div>
      </div>
    );
  }

  // Error state
  if (plansError || subscriptionError) {
    return (
      <div className="max-w-6xl mx-auto">
        <Alert variant="error">
          Failed to load billing information. Please try again later.
        </Alert>
      </div>
    );
  }

  // WP-BILLING-20: `subscription` is the tenant's current subscription, past_due
  // and unpaid included, so the page is gated on it, not on hasActiveSubscription.
  // A customer whose card failed must see their subscription and fix the card,
  // and must never be offered checkout: that creates a second subscription.
  const subscription = subscriptionData?.subscription;
  // WP-BILLING-21: only the owner manages billing; the API refuses members
  // (403), so members get a read-only view instead of buttons that fail.
  const isOwner = user?.role === 'owner';
  // WP-BILLING-19 Part B: an owner can change the plan of a subscription that is
  // active or in trial, paid through Stripe, and not scheduled to cancel (the
  // API refuses the rest with a reason).
  const canChangePlan =
    isOwner &&
    !!subscription &&
    (subscription.status === 'active' || subscription.status === 'trialing') &&
    !subscription.isCouponBased &&
    !subscription.cancelAtPeriodEnd;

  return (
    <div className="max-w-6xl mx-auto space-y-8">
      {/* Page Header */}
      <div>
        <h1 className="text-2xl font-bold text-gray-900">Billing</h1>
        <p className="mt-1 text-gray-600">
          Manage your subscription and billing preferences.
        </p>
      </div>

      {/* WP-BUG-BILLING-SYNC: Show proper state based on sync status */}
      {/* While syncing from checkout, show processing message */}
      {isSyncing && (
        <Alert variant="info">
          <div className="flex items-center gap-2">
            <Spinner size="sm" />
            <span>Processing your subscription...</span>
          </div>
        </Alert>
      )}
      {/* Only show success after sync completes and subscription is confirmed */}
      {checkoutSuccess && !isSyncing && syncComplete && hasSubscription && (
        <Alert variant="success" onDismiss={dismissCheckoutMessage}>
          <strong>Welcome!</strong> Your subscription has been activated successfully.
        </Alert>
      )}
      {/* If sync completed but no subscription yet, webhook may still be processing */}
      {checkoutSuccess && !isSyncing && syncComplete && !hasSubscription && (
        <Alert variant="info" onDismiss={dismissCheckoutMessage}>
          Payment received! Your subscription is being activated. This page will refresh automatically.
        </Alert>
      )}
      {checkoutCanceled && (
        <Alert variant="info" onDismiss={dismissCheckoutMessage}>
          Checkout was canceled. You can try again when you are ready.
        </Alert>
      )}

      {/* WP-BILLING-11: Alert for FREE_FOREVER instant activation */}
      {activationSuccess && (
        <Alert variant="success" onDismiss={() => setActivationSuccess(false)}>
          <strong>Premium Activated!</strong> Your premium subscription is now active. Enjoy all premium features!
        </Alert>
      )}

      {/* Error alert */}
      {error && (
        <Alert variant="error" onDismiss={() => setError(null)}>
          {error}
        </Alert>
      )}

      {/* Current Subscription */}
      {subscription && (
        <CurrentSubscription
          subscription={subscription}
          onCancelSubscription={handleCancelSubscription}
          onResumeSubscription={handleResumeSubscription}
          onManageBilling={handleManageBilling}
          isLoading={cancelSubscription.isPending || resumeSubscription.isPending || billingPortal.isPending}
          canManage={isOwner}
        />
      )}

      {!isOwner && !subscription && (
        <Alert variant="info">
          Billing for this household is managed by its owner. Ask them to choose a plan.
        </Alert>
      )}

      {/* Plan Selection: only for a household without a subscription. WP-BILLING-19:
          buying again would be a second subscription (the API refuses it with
          409); a subscribed household manages its plan from the card above. */}
      {isOwner && !subscription && (
        <Card>
          <CardHeader
            title="Choose a Plan"
            description="Select the plan that best fits your needs"
          />
          {plans && (
            <PlanSelector
              plans={plans}
              onSelectPlan={handleSelectPlan}
              loadingPlanId={loadingPlanId}
            />
          )}
        </Card>
      )}

      {canChangePlan && subscription && (
        <Card>
          <CardHeader
            title="Change Plan"
            description={
              subscription.status === 'trialing'
                ? 'Switch plans now. Your free trial continues, and nothing is charged until it ends.'
                : 'Switch plans now. You will be charged or credited the difference for the rest of this period.'
            }
          />
          {plans && (
            <PlanSelector
              plans={plans}
              currentPlanId={subscription.planId}
              onSelectPlan={handleChooseNewPlan}
              loadingPlanId={loadingPlanId}
              showCoupon={false}
            />
          )}
          <ChangePlanModal
            isOpen={planToConfirm !== null}
            subscription={subscription}
            plan={planToConfirm}
            onClose={() => setPlanToConfirm(null)}
            onConfirm={handleChangePlan}
            isLoading={changePlan.isPending}
          />
        </Card>
      )}

      {/* Payment History - only show if has subscription */}
      {isOwner && subscription && <PaymentHistory />}

      {/* Help section - WP-CONTACT-1: Use ContactLink instead of mailto */}
      <Card>
        <div className="text-center py-4">
          <p className="text-gray-600">
            Have questions about billing?{' '}
            <ContactLink
              topic="billing"
              context="BillingPage"
              className="text-primary-600 hover:text-primary-700 font-medium cursor-pointer"
            >
              Contact support
            </ContactLink>
          </p>
        </div>
      </Card>
    </div>
  );
}
